import type * as React from 'react';
import type { ModalProps } from '../../components/Modal/presenter';
import WithModal from './WithModal';

type AddedProps = {
    modalProps: {
        body: (props: { closeModal?: () => void }) => React.ReactNode;
    } & Omit<ModalProps, 'isOpen' | 'children'>;
    requireExplicitClose?: boolean;
};

/**
 * Decorator that wraps a component in a WithModal component.
 *
 * Usage:
 *
 * ```
 * const MyComponentWithModal = withModal(MyComponent)
 *
 * <MyComponentWithModal
 *      modalProps={{body: ModalBodyComponent, ...otherModalProps}}
 *      requireExplicitClose={boolean}
 *      {...propsForMyComponent}
 * />
 * ```
 *
 * It passes an `onClick` handler to MyComponent that opens the modal.
 * It doesn't forward any props to the Modalbody except for a `closeModal` callback
 */

export default function withModal<T extends { onClick?: React.MouseEventHandler<HTMLElement> }>(
    WrappedComponent: React.ComponentType<T>
): React.ComponentType<T & AddedProps> {
    return (allProps: T & AddedProps) => {
        const {
            modalProps: { body, ...modalProps },
            requireExplicitClose,
            ...props
        } = allProps;
        // @ts-ignore Due to a bug in Typescript, destructuring doesn't work super well right now:
        // - https://github.com/Microsoft/TypeScript/issues/28938
        // - https://github.com/Microsoft/TypeScript/issues/29175
        const wrappedProps: T = props;
        return (
            <WithModal
                noCloseIcon={false}
                {...modalProps}
                requireExplicitClose={requireExplicitClose}
                renderModalContent={body}
            >
                {({ openModal }) => <WrappedComponent {...wrappedProps} onClick={openModal} />}
            </WithModal>
        );
    };
}

export { WithModal };
